import { useContext } from "react";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { InstitutionsContext } from "../firebaseio/InstitutionsContext";
import { BreadAccount } from "breadcommon";
import { RyeButton } from "../rye/RyeButton";
import { buildClasses } from "../utils/buildClasses";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { UserContext } from "../firebaseio/UserContext";
import {
  firestoreDevForceNoAccounts,
  firestoreDevSimulateAccountDisappeared,
  firestoreDeleteAccountById,
} from "../firebaseio/firestoreIo";

export function DevSettings(): JSX.Element {
  const accounts = useContext(AccountsContext);
  const institutions = useContext(InstitutionsContext);
  const user = useContext(UserContext);

  // Group accounts by plaid_item_id
  const accountsByItem = new Map<string, BreadAccount[]>();
  accounts.forEach((account) => {
    if (account.plaid_item_id) {
      if (!accountsByItem.has(account.plaid_item_id)) {
        accountsByItem.set(account.plaid_item_id, []);
      }
      accountsByItem.get(account.plaid_item_id)?.push(account);
    }
  });

  const handleBreakItem = (itemId: string) => {
    console.log(`Forcing Plaid item into error state: ${itemId}`);
    callFirebaseFunction("PlaidSandboxItemResetLogin", {
      plaid_item_id: itemId,
    });
  };

  const handleForceNoAccounts = async (itemId: string) => {
    console.log(`Forcing NO_ACCOUNTS error on Plaid item: ${itemId}`);
    const itemAccounts = Array.from(accounts.values()).filter(
      (a) => a.plaid_item_id === itemId,
    );
    const accountIds = itemAccounts.map((a) => a.id);

    try {
      await firestoreDevForceNoAccounts(user.uid, itemId, accountIds);
    } catch (e) {
      console.error("Failed to force NO_ACCOUNTS error", e);
    }
  };

  const handleSimulateDisappeared = async (accountId: string) => {
    console.log(`Simulating account disappeared from Plaid: ${accountId}`);
    try {
      await firestoreDevSimulateAccountDisappeared(user.uid, accountId);
    } catch (e) {
      console.error("Failed to simulate disappeared account", e);
    }
  };

  const handleDeleteAllAndCleanup = async () => {
    console.log("Deleting all accounts and running cleanup...");
    try {
      for (const account of Array.from(accounts.values())) {
        await firestoreDeleteAccountById(user.uid, account, [], true);
      }
      await callFirebaseFunction("RemoveUnusedItemsAndInstitutions", {});
      console.log("Cleanup complete!");
    } catch (e) {
      console.error("Cleanup failed", e);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-on-surface-500">
        This tab is not displayed in prod!
      </div>
      <div className="flex flex-col gap-4">
        <div className="text-lg font-medium">User Info</div>
        <div className="flex flex-col gap-2">
          <div className="text-sm">User ID: {user.uid}</div>
        </div>
        <div className="text-lg font-medium">Developer Actions</div>
        <div className="flex flex-wrap gap-2">
          <RyeButton
            text="Delete All Accounts & Cleanup Items"
            vibe="danger"
            onClick={handleDeleteAllAndCleanup}
          />
        </div>
        <div className="text-lg font-medium">Plaid Items</div>
        <div className="flex flex-col gap-4">
          {Array.from(accountsByItem.entries()).map(
            ([itemId, itemAccounts]) => {
              const institutionId = itemAccounts[0]?.institution_id;
              const institution = institutionId
                ? institutions.get(institutionId)
                : undefined;
              const institutionName =
                institution?.name ?? "Unknown Institution";
              const isMultiAccount = itemAccounts.length >= 2;

              return (
                <div
                  key={itemId}
                  className={buildClasses(
                    "p-4",
                    "rounded-lg",
                    "border",
                    "border-on-surface-200",
                    "flex",
                    "flex-col",
                    "gap-2",
                  )}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-md">{institutionName}</div>
                    <div className="text-xs text-on-surface-400 font-mono">
                      {itemId}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 text-sm text-on-surface-500">
                    {itemAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex justify-between items-center"
                      >
                        <span>
                          {account.name}
                          {account.mask ? ` (···${account.mask})` : ""}
                        </span>
                        <RyeButton
                          text="Simulate disappeared"
                          vibe="danger"
                          size="sm"
                          disabled={!isMultiAccount}
                          onClick={() => handleSimulateDisappeared(account.id)}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 flex justify-end gap-2">
                    {/* See https://plaid.com/docs/api/sandbox/#sandboxitemreset_login */}
                    <RyeButton
                      text="Force ITEM_LOGIN_REQUIRED (Plaid API)"
                      vibe="danger"
                      size="sm"
                      onClick={() => handleBreakItem(itemId)}
                    />
                    <RyeButton
                      text="Simulate NO_ACCOUNTS_ERROR"
                      vibe="danger"
                      size="sm"
                      onClick={() => handleForceNoAccounts(itemId)}
                    />
                  </div>
                </div>
              );
            },
          )}
          {accountsByItem.size === 0 && (
            <div className="text-on-surface-400 italic">
              No Plaid Items found (linked to accounts).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
